import { computed, ComputedRef } from 'vue';
import { useCategories } from '@ilteducation/content-ref-data';
import type { Category } from '@ilteducation/content-ref-data-types';
import type { CategoryItem } from '@ilteducation/components';
import useAllMaterialsCategory from '@/composables/use-all-materials-category';
import useCategoryTranslation from '@/composables/use-category-translation';

export type MaterialCategoryItem = CategoryItem & { selectIcon: string };

// Admin stores a category's icon already styled, e.g. `fadl fa-box-ballot`, so it is passed through
// untouched. An unset or unregistered icon is expected rather than a bug, hence the fallback.
const CATEGORY_SELECT_ICON_FALLBACK = 'fal fa-file-lines';

const useShortcutMaterialCategories = () => {
  const { categories } = useCategories();
  const { allMaterialsCategory } = useAllMaterialsCategory();
  const { getCategoryTitle, getCategoryDescription } = useCategoryTranslation();

  // `useCategories().load` already scopes to the user's realm + subscription, so here we only keep
  // top-level (no parent) quick-link categories, with the synthetic "All material" card first.
  const materialCategories: ComputedRef<Category[]> = computed(() => [
    allMaterialsCategory.value,
    ...categories.value.filter(
      (category: Category) =>
        (!category.parentCategories || !category.parentCategories.length) && category?.kind === 'shortcut',
    ),
  ]);

  const materialCategoryItems: ComputedRef<MaterialCategoryItem[]> = computed(() =>
    materialCategories.value.map((category) => ({
      id: category.id,
      title: getCategoryTitle(category),
      description: getCategoryDescription(category),
      selectIcon: category.icon || CATEGORY_SELECT_ICON_FALLBACK,
    })),
  );

  return {
    materialCategories,
    materialCategoryItems,
  };
};

export default useShortcutMaterialCategories;
